'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const InquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    destination: '',
    budget: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSubmitted(true);
    setLoading(false);
    
    // Reset form after 5 seconds
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', destination: '', budget: '', message: '' });
    }, 5000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-foreground mb-4">
            Send Us an Inquiry
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Fill out the form below and our travel advisors will get back to you within 24 hours
          </p>
        </div>

        <div className="bg-gradient-to-br from-white to-surface rounded-2xl shadow-3d p-8 lg:p-12 border-2 border-border">
          {submitted ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-trust-green/10 rounded-full mb-6">
                <Icon name="CheckCircleIcon" size={48} className="text-trust-green" />
              </div>
              <h3 className="font-heading text-2xl font-black text-foreground mb-4">
                Thank You for Your Inquiry!
              </h3>
              <p className="text-lg text-muted-foreground mb-2">
                We've received your message and will contact you within 24 hours.
              </p>
              <p className="text-sm text-muted-foreground">
                Check your email for a confirmation message.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="John Smith"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Destination *
                  </label>
                  <input
                    type="text"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all"
                    placeholder="e.g., Dubai, Spain, USA"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Budget (GBP) *
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all"
                  >
                    <option value="">Select budget range</option>
                    <option value="under-500">Under £500</option>
                    <option value="500-1000">£500 - £1,000</option>
                    <option value="1000-2000">£1,000 - £2,000</option>
                    <option value="2000-5000">£2,000 - £5,000</option>
                    <option value="over-5000">Over £5,000</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-foreground mb-2">
                  Additional Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                  placeholder="Tell us more about your travel plans, special requirements, or any questions you have..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-8 py-4 gradient-gold-shiny text-white font-heading font-black rounded-lg shadow-3d border-2 border-accent transition-all duration-300 hover:scale-105 active:scale-95 shine-effect disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Sending...
                  </span>
                ) : (
                  'Submit Inquiry'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default InquiryForm;